/**
 * <bundle-offer data-variant-ids="1,2," data-after-add="checkout|drawer"> — adds every item of a bundle to the cart
 * in one request, then either goes straight to checkout or refreshes the cart sections and opens the cart drawer
 * (the same way the theme's product form does).
 * <bundle-offers> — the track of bundle cards (horizontal swipe on phones, vertical scroll on desktop): dots follow the scroll.
 */
if (!customElements.get('bundle-offer')) {
  class BundleOffer extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-bundle-add]');
      this.error = this.querySelector('.bundle-offer__error');
      this.button?.addEventListener('click', () => this.add());
    }

    async add() {
      if (this.button.getAttribute('aria-disabled') === 'true') return;
      const items = this.dataset.variantIds
        .split(',')
        .filter(Boolean)
        .map((id) => ({ id: Number(id), quantity: 1 }));

      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
      );

      this.button.setAttribute('aria-disabled', 'true');
      this.button.classList.add('btn--loading');
      this.error.hidden = true;
      try {
        const config = FoxTheme.utils.fetchConfig('json');
        config.body = JSON.stringify({ items, sections, sections_url: window.location.pathname });
        const added = await (await fetch(`${FoxTheme.routes.cart_add_url}`, config)).json();
        if (added.status) throw new Error(added.description || added.message);

        // Refresh the cart drawer and count first, so the page is right even when the shopper
        // comes back from checkout with the Back button.
        try {
          const cart = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
          cart.sections = added.sections;
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
          document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: added } }));
        } catch (refreshError) {
          console.warn('bundle-offer: cart refresh failed', refreshError);
        }

        if (this.dataset.afterAdd === 'checkout') {
          window.location.href = '/checkout';
          return;
        }

        const drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.show(this.button);
        else window.location.href = FoxTheme.routes.cart_url;
      } catch (error) {
        this.error.textContent = error.message || 'Could not add this bundle. Please try again.';
        this.error.hidden = false;
      } finally {
        this.button.removeAttribute('aria-disabled');
        this.button.classList.remove('btn--loading');
      }
    }
  }
  customElements.define('bundle-offer', BundleOffer);
}

if (!customElements.get('bundle-offers')) {
  class BundleOffers extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.bundle-offers__track');
      this.cards = [...this.querySelectorAll('bundle-offer')];
      this.dots = [...this.querySelectorAll('.bundle-offers__dot')];
      if (!this.track || this.cards.length < 2) return;
      this.track.addEventListener('scroll', () => this.update(), { passive: true });
      this.update();
    }

    index() {
      const left = this.track.scrollLeft;
      let best = 0;
      this.cards.forEach((card, i) => {
        if (Math.abs(card.offsetLeft - this.track.offsetLeft - left) < Math.abs(this.cards[best].offsetLeft - this.track.offsetLeft - left)) best = i;
      });
      return best;
    }


    update() {
      const i = this.index();
      this.dots.forEach((dot, d) => dot.classList.toggle('is-active', d === i));
    }
  }
  customElements.define('bundle-offers', BundleOffers);
}

/**
 * Back button from checkout shows this page as it was before the bundle was added (empty cart
 * drawer), whether the browser restores it from memory or from its disk cache. On any
 * back/forward arrival, re-read the cart and re-render the cart sections.
 */
window.bundleOffersRefreshCart = async () => {
  const sections = [];
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
  );
  const rendered = sections.length
    ? await (await fetch(`${window.location.pathname}?sections=${sections.join(',')}`, { cache: 'no-store' })).json()
    : {};
  const cart = await (await fetch(`${FoxTheme.routes.cart_url}.js`, { cache: 'no-store' })).json();
  cart.sections = rendered;
  // The theme's cart update moves focus (to the first link when the cart is empty, which reveals
  // the hidden "Skip to content" link). This is a background refresh, so keep focus where it was.
  const focused = document.activeElement;
  FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
  if (document.activeElement !== focused) {
    if (focused && focused !== document.body && document.contains(focused)) focused.focus({ preventScroll: true });
    else document.activeElement?.blur();
  }
};

if (!window.bundleOffersPageshow) {
  window.bundleOffersPageshow = true;
  window.addEventListener('pageshow', (event) => {
    const navigation = performance.getEntriesByType('navigation')[0];
    if (!event.persisted && navigation?.type !== 'back_forward') return;
    if (!window.FoxTheme) return;
    window.bundleOffersRefreshCart().catch(() => window.location.reload());
  });
}
